import { createLogger } from '../utils/logger';
import axiosInstance, { getAuthHeaders } from '../utils/axiosInstance';
import {
  STATES_URL,
  ACTIVITY_DETAILS_URL,
  ACTIVITY_PHOTOS_URL,
  ADD_DAY_ACTIVITIES_URL,
  GENERATE_NEW_DAY_URL,
  ADD_RESTAURANTS_URL,
  DELETE_ACTIVITY_URL,
  MOVE_ACTIVITY_URL,
  SAVE_TRIP_URL,
  DESTINATION_INFO_URL,
  BACKEND_URL,
  REFRESH_DESTINATION_IMAGES_URL
} from '../constants/travel';
import { ActivityDetails, ActivityPhoto } from '../types/chat';
import { requestCache, createCacheKey } from '../utils/requestCache';

const log = createLogger('travelApi');

interface ApiLimitFunctions {
  incrementCallCount: () => void;
}

let apiLimitFunctions: ApiLimitFunctions | null = null;

export const setApiLimitFunctions = (functions: ApiLimitFunctions) => {
  apiLimitFunctions = functions;
};

let currentSessionId: string | null = null;

const generateTraceId = (): string => {
  return `trace_${crypto.randomUUID().replace(/-/g, '')}`;
};

const getSessionId = (): string => {
  if (!currentSessionId) {
    currentSessionId = `session_${crypto.randomUUID().replace(/-/g, '')}`;
    log.debug('Created new session', currentSessionId);
  }
  return currentSessionId;
};

export const resetSession = () => {
  currentSessionId = null;
  log.debug('Session reset');
};

const getTraceHeaders = (customTraceId?: string): Record<string, string> => {
  const traceId = customTraceId || generateTraceId();
  const sessionId = getSessionId();

  return {
    'x-trace-id': traceId,
    'x-session-id': sessionId,
  };
};

const incrementApiCallIfNeeded = () => {
  if (apiLimitFunctions) {
    apiLimitFunctions.incrementCallCount();
  }
};

// Test CORS connectivity
export const testCors = async (): Promise<boolean> => {
  try {
    log.debug('Testing CORS connectivity');
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 
      (process.env.NODE_ENV === 'production' 
        ? 'https://ai-travel-920175119171.europe-west3.run.app'
        : 'http://localhost:8080');
    
    // Try health check first
    const response = await axiosInstance.get(`${baseUrl}/health`);
    log.debug('Health check successful', response.data);
    
    // Try CORS test endpoint
    const corsResponse = await axiosInstance.get(`${baseUrl}/api/cors-test`);
    log.debug('CORS test successful', corsResponse.data);
    
    return true;
  } catch (error) {
    log.error('CORS test failed:', error);
    return false;
  }
};

export const loadAvailableStates = async (): Promise<string[]> => {
  try {
    const response = await axiosInstance.get(STATES_URL);
    return response.data.states;
  } catch (error) {
    log.error('Error loading states:', error);
    return [];
  }
};

export const loadState = async (filename: string) => {
  try {
    const response = await axiosInstance.post(`${STATES_URL}/load`, { filename });
    return response.data.state;
  } catch (error) {
    log.error('Error loading state:', error);
    throw new Error('Failed to load state');
  }
};

export const getActivityDetails = async (
  activityName: string,
  destinationName: string,
  locationName?: string
): Promise<ActivityDetails> => {
  const cacheKey = createCacheKey('activity_details', destinationName, activityName, locationName);

  return requestCache.get(
    cacheKey,
    async () => {
      try {
        const response = await axiosInstance.post(ACTIVITY_DETAILS_URL, {
          activityName,
          destinationName,
          locationName: locationName || activityName
        });
        return response.data as ActivityDetails;
      } catch (error) {
        log.error('Error getting activity details:', error);
        throw new Error('Failed to get additional activity details');
      }
    },
    30 * 60 * 1000 // Cache for 30 minutes
  );
};

export const getActivityPhotos = async (
  activityName: string,
  destinationName: string,
  activityId?: string
): Promise<ActivityPhoto[]> => {
  const cacheKey = createCacheKey('activity_photos', destinationName, activityName, activityId);

  return requestCache.get(
    cacheKey,
    async () => {
      try {
        const response = await axiosInstance.post(ACTIVITY_PHOTOS_URL, {
          activityName,
          destinationName,
          activityId: activityId || undefined
        });

        // Handle new response format with single photo
        if (response.data.photo) {
          return [response.data.photo]; // Wrap single photo in array for compatibility
        }

        // Fallback for old format
        return response.data.photos || [];
      } catch (error: any) {
        log.error('Error getting activity photos:', error);

        if (error.response?.status === 429) {
          log.warn('Rate limited while fetching activity photos', {
            activityName,
            destinationName,
          });
          return [];
        }

        if (error.response?.status === 404) {
          log.warn('Photos endpoint unavailable, using placeholder image', {
            activityName,
            destinationName,
          });
          const searchQuery = `${activityName} ${destinationName}`.trim();
          return [{
            url: `https://via.placeholder.com/800x600/4285f4/ffffff?text=${encodeURIComponent(searchQuery)}`,
            width: 800,
            height: 600,
            attributions: ['Placeholder Image - Photo service temporarily unavailable']
          }];
        }

        throw new Error('Failed to get activity photos');
      }
    },
    60 * 60 * 1000 // Cache for 1 hour (photos don't change)
  );
};

export interface AddDayActivitiesOptions {
  searchId?: string | null;
  numActivities?: number;
}

export interface AddDayActivitiesResponse {
  state: any;
  saveResult?: {
    status: string;
    search_id?: string;
  };
}

export const addDayActivities = async (
  dayNumber: number,
  currentState: any,
  preferredActivityTypes: string[],
  budget: string,
  options: AddDayActivitiesOptions = {}
): Promise<AddDayActivitiesResponse> => {
  try {
    incrementApiCallIfNeeded();
    const traceHeaders = getTraceHeaders();
    log.info('Adding day activities', {
      dayNumber,
      searchId: options.searchId,
      numActivities: options.numActivities,
    });

    const payload: Record<string, any> = {
      day_number: dayNumber,
      state: currentState,
      preferred_activity_types: preferredActivityTypes,
      budget: budget
    };

    if (options.searchId) {
      payload.search_id = options.searchId;
    }
    if (options.numActivities && options.numActivities > 0) {
      payload.num_activities = options.numActivities;
    }

    const response = await axiosInstance.post(ADD_DAY_ACTIVITIES_URL, payload, {
      headers: traceHeaders
    });
    return {
      state: response.data.state,
      saveResult: response.data.save_result
    };
  } catch (error: any) {
    log.error(`Error adding activities to Day ${dayNumber}:`, error);
    throw new Error(`Failed to add activities: ${error.response?.data?.detail || error.message}`);
  }
};

export interface GenerateNewDayOptions {
  searchId?: string | null;
  numActivities?: number;
}

export interface GenerateNewDayResponse {
  state: any;
  saveResult?: {
    status: string;
    search_id?: string;
  };
}

export const generateNewDay = async (
  currentState: any,
  preferredActivityTypes: string[],
  budget: string,
  options: GenerateNewDayOptions = {}
): Promise<GenerateNewDayResponse> => {
  try {
    incrementApiCallIfNeeded();
    const traceHeaders = getTraceHeaders();
    log.info('Generating new day', {
      preferredActivityTypes,
      searchId: options.searchId,
      numActivities: options.numActivities,
    });

    const payload: Record<string, any> = {
      state: currentState,
      preferred_activity_types: preferredActivityTypes,
      budget: budget
    };

    if (options.searchId) {
      payload.search_id = options.searchId;
    }
    if (options.numActivities && options.numActivities > 0) {
      payload.num_activities = options.numActivities;
    }

    const response = await axiosInstance.post(GENERATE_NEW_DAY_URL, payload, {
      headers: traceHeaders
    });
    return {
      state: response.data.state,
      saveResult: response.data.save_result
    };
  } catch (error: any) {
    log.error('Error generating new day:', error);
    throw new Error(`Failed to generate new day: ${error.response?.data?.detail || error.message}`);
  }
};

export interface AddRestaurantsOptions {
  searchId?: string | null;
  numRestaurants?: number;
  dietaryRestrictions?: string;
  additionalInfo?: string | null;
  initialQuery?: string | null;
}

export interface AddRestaurantsResponse {
  state: any;
  saveResult?: {
    status: string;
    search_id?: string;
  };
}

export const addRestaurants = async (
  currentState: any,
  budget: string,
  options: AddRestaurantsOptions = {}
): Promise<AddRestaurantsResponse> => {
  try {
    incrementApiCallIfNeeded();
    const traceHeaders = getTraceHeaders();
    log.info('Adding restaurants', {
      requested: options.numRestaurants,
      dietaryRestrictions: options.dietaryRestrictions,
      searchId: options.searchId,
    });

    const payload: Record<string, any> = {
      state: currentState,
      budget: budget
    };

    if (options.searchId) {
      payload.search_id = options.searchId;
    }
    if (options.numRestaurants && options.numRestaurants > 0) {
      payload.num_restaurants = options.numRestaurants;
    }
    if (options.dietaryRestrictions) {
      payload.dietary_restrictions = options.dietaryRestrictions;
    }
    if (options.additionalInfo || currentState?.user_additional_info) {
      payload.additional_info = options.additionalInfo || currentState?.user_additional_info;
    }
    if (options.initialQuery || currentState?.initial_query) {
      payload.initial_query = options.initialQuery || currentState?.initial_query;
    }

    const response = await axiosInstance.post(ADD_RESTAURANTS_URL, payload, {
      headers: traceHeaders
    });
    return {
      state: response.data.state,
      saveResult: response.data.save_result
    };
  } catch (error: any) {
    log.error('Error adding restaurants:', error);
    throw new Error(`Failed to add restaurants: ${error.response?.data?.detail || error.message}`);
  }
};

export const deleteActivity = async (activityId: string, currentState: any) => {
  try {
    const response = await axiosInstance.post(DELETE_ACTIVITY_URL, {
      activity_id: activityId,
      state: currentState
    });
    return {
      state: response.data.state,
      saveResult: response.data.save_result
    };
  } catch (error: any) {
    log.error(`Error deleting activity ${activityId}:`, error);
    throw new Error(`Failed to delete activity: ${error.response?.data?.detail || error.message}`);
  }
};

export const moveActivity = async (
  activityId: string,
  targetDay: number,
  targetTimePeriod: string,
  currentState: any,
  sourceDayIndex?: number
) => {
  try {
    const response = await axiosInstance.post(MOVE_ACTIVITY_URL, {
      activity_id: activityId,
      target_day: targetDay,
      target_time_period: targetTimePeriod,
      source_day: sourceDayIndex,
      state: currentState
    });
    return response.data.state;
  } catch (error: any) {
    log.error(`Error moving activity ${activityId}:`, error);
    throw new Error(`Failed to move activity: ${error.response?.data?.detail || error.message}`);
  }
};

export const saveTrip = async (currentState: any, parentSearchId?: string) => {
  try {
    const response = await axiosInstance.post(SAVE_TRIP_URL, {
      state: currentState,
      parent_search_id: parentSearchId
    });
    return {
      searchId: response.data.search_id,
      status: response.data.status
    };
  } catch (error: any) {
    log.error('Error saving trip:', error);
    throw new Error(`Failed to save trip: ${error.response?.data?.detail || error.message}`);
  }
};

export const downloadCurrentState = (stateData: any) => {
  const blob = new Blob([JSON.stringify(stateData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `travel_plan_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const generateTravelPlan = async (
  tripParameters: any,
  currentState?: any,
  conversationId?: string | null,
  confirmCreation: boolean = false
): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
  incrementApiCallIfNeeded();

  log.info('generateTravelPlan request', {
    confirmCreation,
    hasState: Boolean(currentState),
    destination: tripParameters?.destination,
  });

  if (!currentState) {
    resetSession();
  }

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const startDate = tripParameters.startDate || formatDate(today);
  const endDate = tripParameters.endDate || formatDate(tomorrow);

  const traceHeaders = getTraceHeaders();
  const authHeaders = await getAuthHeaders();

  // Build the request body
  const requestBody = {
    destination: tripParameters.destination,
    start_date: startDate,
    end_date: endDate,
    budget: tripParameters.budget || null,
    preferred_activity_types: tripParameters.preferredActivityTypes.length > 0 ? tripParameters.preferredActivityTypes : null,
    preferred_transportation_types: tripParameters.preferredTransportationTypes.length > 0 ? tripParameters.preferredTransportationTypes : null,
    number_of_days: tripParameters.numberOfDays || null,
    state: currentState,
    additional_info: tripParameters.additionalInfo || null,
    conversation_id: conversationId || undefined,
    confirm_creation: confirmCreation === true
  };

  // Call the Node.js server instead of trying to authenticate directly with Python backend
  let response: Response;
  try {
    response = await fetch(BACKEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...traceHeaders,
        ...authHeaders,
      },
      credentials: 'include',
      body: JSON.stringify(requestBody)
    });
  } catch (error: any) {
    log.error('Network error during fetch:', error);
    // Check if it's a CORS error
    if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
      throw new Error(
        `CORS or network error: Unable to connect to backend at ${BACKEND_URL}. ` +
        `Please check that the backend is running and CORS is configured correctly. ` +
        `If deployed, ensure FRONTEND_URL environment variable is set on the backend.`
      );
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to server'}`);
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    log.error(`Server error: ${response.status} - ${errorText}`);
    throw new Error(
      `Server responded with status ${response.status}: ${errorText || 'Unknown error'}`
    );
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Failed to get response reader');
  }

  return reader;
};

export const sendFollowUpMessage = async (
  followUpMessage: string,
  lastMessageData: any,
  conversationHistory?: any[],
  conversationId?: string | null,
  confirmCreation: boolean = false,
  searchId?: string | null
): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
  incrementApiCallIfNeeded();

  const traceHeaders = getTraceHeaders();
  const authHeaders = await getAuthHeaders();
  const isNewTripRequest =
    /\b(generate|create|plan|make)\b.*\b(trip|itinerary)\b/i.test(followUpMessage) ||
    /\b(generate|create|plan)\s+it\s+(for|to|fot)\s+/i.test(followUpMessage) ||
    /\b(travel|visit|go)\s+to\b/i.test(followUpMessage) ||
    /\bmake\s+it\s+a\s+trip\s+to\b/i.test(followUpMessage);

  log.info('Sending follow-up message', {
    isNewTripRequest,
    hasState: Boolean(lastMessageData),
    confirmCreation,
  });

  // For new trip requests, reset session and let backend router agent do ALL enrichment
  if (isNewTripRequest) {
    log.info('Detected new trip request, resetting session and delegating to backend router');
    resetSession();
  }

  log.debug('Sending query to backend for router enrichment', {
    query: followUpMessage,
    hasState: Boolean(lastMessageData) && !isNewTripRequest,
  });

  // Send minimal payload - router agent will extract and enrich everything
  const requestPayload: any = {
    query: followUpMessage,
    destination: '', // Router will extract this
    state: isNewTripRequest ? undefined : lastMessageData,
    conversation_history: isNewTripRequest ? [] : (conversationHistory || []),
    conversation_id: conversationId || undefined,
    confirm_creation: confirmCreation === true,
    search_id: searchId || undefined,
  };

  // Call the Node.js server instead of trying to authenticate directly with Python backend
  let response: Response;
  try {
    response = await fetch(BACKEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...traceHeaders,
        ...authHeaders,
      },
      credentials: 'include',
      body: JSON.stringify(requestPayload)
    });
  } catch (error: any) {
    log.error('Network error during fetch:', error);
    // Check if it's a CORS error
    if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
      throw new Error(
        `CORS or network error: Unable to connect to backend at ${BACKEND_URL}. ` +
        `Please check that the backend is running and CORS is configured correctly. ` +
        `If deployed, ensure FRONTEND_URL environment variable is set on the backend.`
      );
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to server'}`);
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    log.error(`Server error: ${response.status} - ${errorText}`);
    throw new Error(
      `Server responded with status ${response.status}: ${errorText || 'Unknown error'}`
    );
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Failed to get response reader');
  }

  return reader;
};

/**
 * Get destination information via standalone endpoint.
 *
 * NOTE: Currently unused - destination info is streamed during trip generation
 * in the main /api/travel flow. This function is maintained for potential
 * standalone destination queries in the future.
 */
export const getDestinationInfo = async (destination: string) => {
  const cacheKey = createCacheKey('destination_info', destination);

  return requestCache.get(
    cacheKey,
    async () => {
      try {
        const traceHeaders = getTraceHeaders();
        log.info('Fetching destination info', { destination });

        const response = await axiosInstance.post(DESTINATION_INFO_URL, {
          destination
        }, {
          headers: traceHeaders
        });
        return response.data;
      } catch (error: any) {
        log.error('Error getting destination info:', error);
        throw new Error(`Failed to get destination information: ${error.response?.data?.detail || error.message}`);
      }
    },
    10 * 60 * 1000 // Cache for 10 minutes
  );
};

export interface RefreshDestinationImagesResponse {
  message: string;
  image_urls: string[];
  total: number;
}

export const refreshDestinationImages = async (destination: string): Promise<RefreshDestinationImagesResponse> => {
  try {
    const traceHeaders = getTraceHeaders();
    log.info('Refreshing destination images', { destination });

    const response = await axiosInstance.post(`${REFRESH_DESTINATION_IMAGES_URL}/${encodeURIComponent(destination)}`, {}, {
      headers: traceHeaders
    });

    // Clear the cache for activity photos with destination-hero ID
    const cacheKey = createCacheKey('activity_photos', destination, 'Zanzibar Tanzania', 'destination-hero');
    requestCache.invalidate(cacheKey);

    log.info('Destination images refreshed successfully', { destination, imageCount: response.data.total });
    return response.data;
  } catch (error: any) {
    log.error('Error refreshing destination images:', error);
    throw new Error(`Failed to refresh destination images: ${error.response?.data?.detail || error.message}`);
  }
};

// ============================================
// MCP Integration API Functions
// ============================================

const MCP_BASE_URL = `${process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://ai-travel-920175119171.europe-west3.run.app'
    : 'http://localhost:8080')}/api/mcp`;

export interface MCPServer {
  name: string;
  command: string;
  enabled: boolean;
  description: string;
}

export interface MCPTool {
  name: string;
  description: string;
  category: string;
  parameters: string[];
}

export interface MCPServersResponse {
  servers: Record<string, MCPServer>;
  count: number;
}

export interface MCPToolsResponse {
  tools: MCPTool[];
  tools_by_category: Record<string, MCPTool[]>;
  count: number;
  categories: string[];
}

export interface MCPStatusResponse {
  enabled: boolean;
  servers_count: number;
  tools_count: number;
  servers: string[];
  status: 'operational' | 'no_servers_configured';
}

/**
 * Get information about enabled MCP servers
 */
export const getMCPServers = async (): Promise<MCPServersResponse> => {
  try {
    const response = await axiosInstance.get(`${MCP_BASE_URL}/servers`);
    return response.data;
  } catch (error: any) {
    log.error('Error getting MCP servers:', error);
    throw new Error(`Failed to get MCP servers: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Get all available MCP tools, optionally filtered by category
 */
export const getMCPTools = async (category?: string): Promise<MCPToolsResponse> => {
  try {
    const url = category
      ? `${MCP_BASE_URL}/tools?category=${encodeURIComponent(category)}`
      : `${MCP_BASE_URL}/tools`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error: any) {
    log.error('Error getting MCP tools:', error);
    throw new Error(`Failed to get MCP tools: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Get detailed information about a specific MCP tool
 */
export const getMCPToolDetails = async (toolName: string): Promise<MCPTool> => {
  try {
    const response = await axiosInstance.get(`${MCP_BASE_URL}/tools/${toolName}`);
    return response.data;
  } catch (error: any) {
    log.error(`Error getting MCP tool details for ${toolName}:`, error);
    throw new Error(`Failed to get tool details: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Get overall MCP integration status
 */
export const getMCPStatus = async (): Promise<MCPStatusResponse> => {
  try {
    const response = await axiosInstance.get(`${MCP_BASE_URL}/status`);
    return response.data;
  } catch (error: any) {
    log.error('Error getting MCP status:', error);
    throw new Error(`Failed to get MCP status: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Call an MCP tool with arguments
 */
export const callMCPTool = async (toolName: string, args: Record<string, any>): Promise<any> => {
  try {
    const response = await axiosInstance.post(`${MCP_BASE_URL}/call`, {
      tool_name: toolName,
      arguments: args
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'MCP tool call failed');
    }

    return response.data.result;
  } catch (error: any) {
    log.error(`Error calling MCP tool ${toolName}:`, error);
    throw new Error(`Failed to call MCP tool: ${error.response?.data?.detail || error.message}`);
  }
};
